import {
  FEED_PROVIDERS,
  SIGNAL_SEVERITY_BANDS,
  SIGNAL_TRIAGE_STATES,
  type Signal,
  signalDismissSchema,
} from '@fruuit/shared'
import {
  Callout,
  Card,
  type Column,
  DataTable,
  EmptyState,
  Input,
  PageHeader,
  Select,
  useDialog,
  useToast,
} from '@fruuit/ui'
import { Radar } from 'lucide-react'
import { useDeferredValue, useEffect, useRef, useState } from 'react'
import { errorMessage } from '../../api/client'
import { formatDate, formatDateTime } from '../../features/feeds/format'
import { useCursorPager } from '../../features/feeds/useCursorPager'
import {
  type SignalFilters,
  useDismissSignal,
  usePromoteSignal,
  useSignals,
} from '../../features/signals/api'
import {
  NoveltyBadge,
  ScorePill,
  SignalDetail,
  TriageBadge,
} from '../../features/signals/components'
import { ReasonDialog } from '../../features/subscriptions/ReasonDialog'
import { useTitle } from '../../lib/useTitle'

const PROVIDER_OPTIONS = [
  { value: '', label: 'All providers' },
  ...FEED_PROVIDERS.values.map((value) => ({ value, label: FEED_PROVIDERS.labels[value] })),
]
const SEVERITY_OPTIONS = [
  { value: '', label: 'Any severity' },
  ...SIGNAL_SEVERITY_BANDS.map((value) => ({
    value,
    label: value.charAt(0).toUpperCase() + value.slice(1),
  })),
]
const STATUS_OPTIONS = [
  { value: '', label: 'All states' },
  ...SIGNAL_TRIAGE_STATES.values.map((value) => ({
    value,
    label: SIGNAL_TRIAGE_STATES.labels[value],
  })),
]

const NOTE_SCHEMA = signalDismissSchema.shape.reason

const COLUMNS: Column<Signal>[] = [
  {
    id: 'score',
    header: 'Score',
    cell: (signal) => <ScorePill score={signal.signal_score} />,
  },
  {
    id: 'signal',
    header: 'Signal',
    cell: (signal) => (
      <div className="min-w-[15rem] max-w-lg">
        <p className="font-semibold leading-snug text-ink">{signal.title}</p>
        <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-sage">
          <NoveltyBadge novelty={signal.novelty} />
          <span>{signal.provider_name}</span>
        </p>
        <p className="mt-1 text-xs text-sage">
          Event{' '}
          <span className={signal.event_date ? 'text-ink' : 'italic'}>
            {formatDate(signal.event_date)}
          </span>{' '}
          · Delivered{' '}
          <span className="text-ink">{formatDate(signal.delivered_at ?? signal.retrieved_at)}</span>
        </p>
      </div>
    ),
  },
  {
    id: 'issuer',
    header: 'Issuer',
    cell: (signal) =>
      signal.issuer ? (
        <div className="min-w-[9rem]">
          <p className="text-ink">{signal.issuer.legal_name}</p>
          <p className="text-xs text-sage">{signal.issuer.country}</p>
        </div>
      ) : (
        <span className="text-sm font-semibold text-watch">Unmapped</span>
      ),
  },
  {
    id: 'triage',
    header: 'Triage',
    className: 'whitespace-nowrap',
    cell: (signal) => <TriageBadge state={signal.triage_state} />,
  },
]

type TriageAction = { mode: 'dismiss' | 'promote'; signal: Signal }

/** D-06 Signals inbox: feed signals are candidates for human assessment (P-FEED-003). */
export function SignalsPage() {
  useTitle('Signals')
  const { toast } = useToast()
  const [filters, setFilters] = useState<SignalFilters>({
    provider: '',
    severity: '',
    status: 'new',
    q: '',
  })
  const deferredQ = useDeferredValue(filters.q)
  const pager = useCursorPager()
  const signals = useSignals({ ...filters, q: deferredQ }, pager.cursor)
  const [selected, setSelected] = useState<Signal | null>(null)
  const detailRef = useRef<HTMLDivElement>(null)
  const dialog = useDialog<TriageAction>()
  const dismiss = useDismissSignal()
  const promote = usePromoteSignal()
  const action = dialog.data

  // Keep the open detail in sync with refreshed list data.
  useEffect(() => {
    if (!selected) return
    const fresh = signals.data?.items.find((item) => item.id === selected.id)
    if (fresh && fresh.revision !== selected.revision) setSelected(fresh)
  }, [signals.data, selected])

  function update(patch: Partial<SignalFilters>) {
    setFilters((current) => ({ ...current, ...patch }))
    pager.reset()
  }

  function select(signal: Signal) {
    setSelected(signal)
    if (window.matchMedia?.('(max-width: 1279px)').matches) {
      requestAnimationFrame(() => detailRef.current?.scrollIntoView?.({ behavior: 'smooth' }))
    }
  }

  async function confirm(text: string) {
    if (!action) return
    const { signal } = action
    if (action.mode === 'dismiss') {
      const result = await dialog.guard.run(() =>
        dismiss.mutateAsync({ id: signal.id, revision: signal.revision, reason: text }),
      )
      if (result.stale) return
      dialog.close()
      setSelected(result.value)
      toast({ tone: 'success', message: 'Signal dismissed. The reason is in the audit trail.' })
      return
    }
    const result = await dialog.guard.run(() =>
      promote.mutateAsync({
        id: signal.id,
        revision: signal.revision,
        ...(text ? { note: text } : {}),
      }),
    )
    if (result.stale) return
    dialog.close()
    setSelected(result.value.signal)
    toast({
      tone: 'success',
      title: 'Draft incident created',
      message: `“${result.value.incident.title}” is a draft for assessment work. Nothing was published.`,
    })
  }

  return (
    <>
      <PageHeader
        eyebrow="Feed signals"
        title="Leads, not conclusions."
        description="Signals from Fruuit’s AI-assisted screening engine (FactSet raw data, cross-validated with Sustainalytics), News Monitor and Company Disclosures wait here for a human decision. Dismiss them with a reason or promote them into a draft incident. Signals are never published automatically and never set findings."
      />

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Input
          label="Search"
          type="search"
          placeholder="Title or summary"
          value={filters.q}
          onChange={(event) => update({ q: event.target.value })}
        />
        <Select
          label="Provider"
          options={PROVIDER_OPTIONS}
          value={filters.provider}
          onChange={(event) => update({ provider: event.target.value })}
        />
        <Select
          label="Severity"
          options={SEVERITY_OPTIONS}
          value={filters.severity}
          onChange={(event) => update({ severity: event.target.value })}
        />
        <Select
          label="Triage state"
          options={STATUS_OPTIONS}
          value={filters.status}
          onChange={(event) => update({ status: event.target.value })}
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_26rem]">
        <div className="min-w-0">
          {signals.isError ? (
            <Callout tone="danger" role="alert">
              {errorMessage(signals.error)}
            </Callout>
          ) : (
            <DataTable
              caption="Feed signals awaiting triage"
              columns={COLUMNS}
              rows={signals.data?.items ?? []}
              getRowId={(signal) => signal.id}
              loading={signals.isPending || signals.isPlaceholderData}
              onRowClick={select}
              empty={
                <EmptyState
                  variant="plain"
                  icon={<Radar />}
                  title="No signals match"
                  description="New signals arrive with each feed run. An empty inbox is not evidence that there is no concern."
                />
              }
              pagination={{
                mode: 'server',
                hasPrevious: pager.hasPrevious,
                hasNext: Boolean(signals.data?.next_cursor),
                onPrevious: pager.previous,
                onNext: () => pager.next(signals.data?.next_cursor ?? null),
                summary: `Page ${pager.page}`,
              }}
            />
          )}
        </div>

        <div ref={detailRef} className="min-w-0 xl:sticky xl:top-6 xl:self-start">
          {selected ? (
            <Card padding="md">
              <SignalDetail
                signal={selected}
                onDismiss={() => dialog.open({ mode: 'dismiss', signal: selected })}
                onPromote={() => dialog.open({ mode: 'promote', signal: selected })}
              />
            </Card>
          ) : (
            <Card variant="tint">
              <EmptyState
                variant="plain"
                icon={<Radar />}
                title="Select a signal"
                description="See where it came from, which identifier matched it and the event it links to."
              />
            </Card>
          )}
        </div>
      </div>

      <p className="mt-6 text-xs text-sage">
        Times in UTC. Last refreshed{' '}
        {formatDateTime(new Date(signals.dataUpdatedAt || Date.now()).toISOString())}.
      </p>

      {dialog.isOpen && action ? (
        <ReasonDialog
          open
          onClose={dialog.close}
          title={action.mode === 'dismiss' ? 'Dismiss this signal' : 'Promote to a draft incident'}
          description={
            action.mode === 'dismiss'
              ? 'The signal leaves the inbox. It stays on record with your reason.'
              : 'Creates a draft incident linked to this signal for assessment work. Nothing is published or shown to investors.'
          }
          label={action.mode === 'dismiss' ? 'Reason' : 'Note'}
          hint={
            action.mode === 'dismiss'
              ? 'At least 8 characters, for example "Routine corporate news, no norms content".'
              : 'Optional. At least 8 characters if given.'
          }
          optional={action.mode === 'promote'}
          schema={NOTE_SCHEMA}
          confirmLabel={action.mode === 'dismiss' ? 'Dismiss signal' : 'Create draft incident'}
          onConfirm={confirm}
        />
      ) : null}
    </>
  )
}
